import { useEffect, useRef, useState } from 'react';

export interface PollState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
}

/**
 * Runs `load` now and every `intervalMs`, keeping the last good data on failure so a
 * flaky network or rate limit never blanks a widget. After a failure it tries again
 * sooner – 1 min, then 2, 4… up to `intervalMs` – so a brief outage clears quickly.
 * Re-runs when `key` changes.
 */
export function usePolling<T>(
  key: string | null,
  load: (signal: AbortSignal) => Promise<T>,
  intervalMs: number,
): PollState<T> {
  const [state, setState] = useState<PollState<T>>({ data: null, error: null, loading: key !== null });
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (key === null) {
      setState({ data: null, error: null, loading: false });
      return;
    }
    let controller = new AbortController();
    let cancelled = false;
    let retry: number | undefined;
    let failures = 0;
    setState((s) => ({ ...s, loading: true }));

    const run = async () => {
      controller.abort();
      controller = new AbortController();
      try {
        const data = await loadRef.current(controller.signal);
        failures = 0;
        if (!cancelled) setState({ data, error: null, loading: false });
      } catch (e) {
        if (cancelled || (e instanceof DOMException && e.name === 'AbortError')) return;
        setState((s) => ({ ...s, error: e instanceof Error ? e.message : 'Unavailable', loading: false }));
        window.clearTimeout(retry);
        const wait = Math.min(intervalMs, 60_000 * 2 ** failures++);
        if (wait < intervalMs) retry = window.setTimeout(() => void run(), wait);
      }
    };

    void run();
    const timer = window.setInterval(run, intervalMs);
    const onOnline = () => void run();
    window.addEventListener('online', onOnline);
    return () => {
      cancelled = true;
      controller.abort();
      window.clearInterval(timer);
      window.clearTimeout(retry);
      window.removeEventListener('online', onOnline);
    };
  }, [key, intervalMs]);

  return state;
}
